/**
 * useUserQuests - Hook for fetching user completed quests
 */

import { useCallback, useEffect, useState } from 'react';
import { useApi } from './useApi';
import { getUserQuests } from '../api/services/questService';

/**
 * Hook to fetch user's completed quests
 * @param {string} userAddress - User's wallet address
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Fetch immediately when address is available (default: false)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @returns {Object} - { quests, loading, error, refetch }
 */
export const useUserQuests = (userAddress, options = {}) => {
  const {
    immediate = false,
    onSuccess,
    onError,
  } = options;

  const fetchUserQuests = useCallback(
    () => getUserQuests(userAddress),
    [userAddress]
  );

  const { data, loading, error, execute, reset } = useApi(fetchUserQuests, {
    immediate: false,
    initialData: null,
    onSuccess,
    onError,
  });

  // Trigger fetch when address becomes available
  useEffect(() => {
    if (userAddress && immediate) {
      execute();
    }
  }, [userAddress, immediate, execute]);

  // Extract quests from response
  const quests = data?.quests || [];

  return {
    quests,
    loading,
    error,
    refetch: execute,
    reset,
  };
};

export default useUserQuests;
